import { TextAreaEditor, TextEditor } from '@approvalmax/ui';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { getFieldsApprovalPermissionBySystemPurpose } from 'pages/requestForm/selectors/requestSelectors';
import { FC, memo, useCallback } from 'react';

import { editRequestField } from '../../../../../actions';
import { getShowValidationErrors } from '../../../../../selectors/pageSelectors';
import { messages } from './XeroQuoteTextInfoSection.messages';
import { Root, SummaryField, TitleField } from './XeroQuoteTextInfoSection.styles';

const { xeroConstants } = constants;

type XeroQuoteTextInfoSectionProps = {
    request: domain.XeroQuoteRequest;
};

const XeroQuoteTextInfoSection: FC<XeroQuoteTextInfoSectionProps> = memo((props) => {
    const { request } = props;
    const dispatch = useDispatch();
    const showValidationErrors = useSelector(getShowValidationErrors);
    const [titleFieldPermission, summaryFieldPermission] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [
            domain.FieldSystemPurpose.XeroQuoteTitle,
            domain.FieldSystemPurpose.XeroQuoteSummary,
        ])
    );

    const { title, summary } = request.details;
    const isTitleInvalid =
        showValidationErrors &&
        ((!!title && title.length > xeroConstants.TITLE_MAX_LENGTH) || (titleFieldPermission.required && !title));
    const isSummaryInvalid =
        showValidationErrors &&
        ((!!summary && summary.length > xeroConstants.SUMMARY_MAX_LENGTH) ||
            (summaryFieldPermission.required && !summary));

    const onChangeTitle = useCallback(
        (title: string) => {
            dispatch(editRequestField<domain.XeroQuoteDetails>('title', title));
        },
        [dispatch]
    );

    const onChangeSummary = useCallback(
        (summary: string) => {
            dispatch(editRequestField<domain.XeroQuoteDetails>('summary', summary));
        },
        [dispatch]
    );

    return (
        <Root>
            <TitleField title={messages.titleFieldTitle} required={titleFieldPermission.required}>
                <TextEditor
                    invalid={isTitleInvalid}
                    value={title || ''}
                    maxLength={xeroConstants.TITLE_MAX_LENGTH}
                    onChange={onChangeTitle}
                    placeholder={messages.titleFieldPlaceholder}
                    disabled={titleFieldPermission.disabled}
                />
            </TitleField>

            <SummaryField title={messages.summaryFieldTitle} required={summaryFieldPermission.required}>
                <TextAreaEditor
                    invalid={isSummaryInvalid}
                    value={summary || ''}
                    maxLength={xeroConstants.SUMMARY_MAX_LENGTH}
                    onChange={onChangeSummary}
                    placeholder={messages.summaryFieldPlaceholder}
                    disabled={summaryFieldPermission.disabled}
                />
            </SummaryField>
        </Root>
    );
});

export default XeroQuoteTextInfoSection;
